import { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import getConfig from "next/config";
import { Form, Field } from "react-final-form";

import { addModal, removeModal } from "@store/app/actions";
import Breadcrumbs from "@components/Breadcrumbs";
import Heading from "@components/Heading";
import Button from "@components/Button";
import Modal from "@components/Modal";
import InputCheckbox from "@components/Form/Checkbox";
import InputText from "@components/Form/InputText";
import {
  composeFormValidators,
  isRequired,
  isAlphaNumeric,
  isPhoneNumber,
  hasMaxLength,
} from "@lib/validations";
import { getSlug } from "@lib/helpers";
import { content } from "@lib/careersContent";
import replaceAll from "string.prototype.replaceall";

import Accordion from "@components/Accordion";
import AccordionItem from "@components/Accordion/Item";
import AccordionItemHeader from "@components/Accordion/Item/Header";
import AccordionItemBody from "@components/Accordion/Item/Body";
import { useRouter } from "next/router";

const {
  publicRuntimeConfig: {
    content: { websiteTitle },
    settings: { websiteName, baseUrl, cdn },
  },
} = getConfig();

const CareersContainer = ({
  departmentsWithJobPositions,
  addModal,
  removeModal,
}) => {
  const { asPath } = useRouter();
  const [openedSection, setOpenedSection] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("Toate");
  const [cityFilter, setCityFilter] = useState("Toate");
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [jobApplicationFormSubmitHandler, setJobApplicationFormSubmitHandler] =
    useState(undefined);

  const getPositionsCityNames = () => [
    ...new Set(
      departmentsWithJobPositions
        .map((department) =>
          department.jobs.map(
            (job) =>
              job.locations && job.locations.map((location) => location.city)
          )
        )
        // TODO: Replace with flat(2) if node >= 11 or new Babel version
        .reduce((acc, val) => acc.concat(...val), [])
        .sort()
    ),
  ];

  const getDepartmentNames = () =>
    departmentsWithJobPositions
      .map((department) => department.department_name)
      .sort();

  const filterDepartmentsByName = (name, departments) =>
    departments.filter((department) => department.department_name === name);

  const filterDepartmentsByCity = (city, departments) =>
    departments.reduce((filteredDepartments, department) => {
      const jobsInCity = department.jobs.filter(
        (job) =>
          job.locations &&
          job.locations.map((location) => location.city).includes(city)
      );

      return jobsInCity.length
        ? [
            ...filteredDepartments,
            {
              ...department,
              jobs: jobsInCity,
            },
          ]
        : filteredDepartments;
    }, []);

  const handleDepartmentFilterChange = (event) => {
    setDepartmentFilter(event.target.value);
  };

  const handleCityFilterChange = (event) => {
    setCityFilter(event.target.value);
  };

  const handleJobApplicationFormSubmit = (values) => {
    const { lastName, firstName, phoneNumber } = values;
    const { jobInfo, location } = selectedPosition;

    const emailSubject = encodeURIComponent(
      `[${location.city.toUpperCase()}] [${jobInfo.name.toUpperCase()}] ${
        values.firstName
      } ${values.lastName}`
    );
    const emailBody = encodeURIComponent(`Buna ziua,

Inaintand acest e-mail veti reusi sa aplicati cu succes la postul vacant si va prezentam mai jos etapele procesului de recrutare:

1. Depunerea documentelor de candidatură

Vă rugăm să transmiteți următoarele documente la adresa: recrutare@altex.ro

Curriculum Vitae (CV);

Scrisoare de intenție;

Scrisori de recomandare de la doi foști angajatori – reprezintă un avantaj.

Echipa noastră de Resurse Umane va evalua îndeplinirea condițiilor minime de experiență și competență, conform cerințelor specifice postului.

2. Etapa de testare

În termen de 24 de ore de la trimiterea documentelor, vă rugăm să accesați și să completați testul disponibil la următorul link: https://forms.office.com/e/C6rUBLjQL4

3. Interviul final

Candidații care promovează etapa de testare vor fi contactați pentru a stabili un interviu final cu echipa de recrutare.

Detaliile postului pentru care ați aplicat:
Poziție: ${jobInfo.name}

Locație: ${location.city}

Nume: ${lastName} ${firstName}

Telefon: ${phoneNumber}

Consimțământ pentru prelucrarea datelor:
Acord pentru prelucrarea datelor cu caracter personal de către Altex: DA

Acord pentru prelucrarea datelor cu caracter personal de către partenerii Altex: DA

Cu respect,
Echipa Altex`);

    const mail = document.createElement("a");
    mail.style.display = "none";
    mail.href = `mailto:${replaceAll(
      location.emails,
      "\\s+",
      ""
    )}?subject=${emailSubject}&body=${emailBody}`;
    mail.target = "_blank";

    mail.click();
  };

  const handleSelectJobTitle = ({ job, location }) => {
    const { ...jobInfo } = job;
    setSelectedPosition({ jobInfo, location });
    addModal("jobApplication");
  };

  const handleCancelJobApplication = () => {
    setSelectedPosition(null);
    removeModal("jobApplication");
  };

  const handleSubmitJobApplication = () => {
    jobApplicationFormSubmitHandler();
  };

  const onAccordionItemToggle = ({ item, expanded }) =>
    history.replaceState({}, "", `#${expanded ? item : ""}`);

  const filteredDepartmentsByName =
    departmentFilter === "Toate"
      ? departmentsWithJobPositions
      : filterDepartmentsByName(departmentFilter, departmentsWithJobPositions);

  const filteredDepartmentsWithJobsByCity =
    cityFilter === "Toate"
      ? filteredDepartmentsByName
      : filterDepartmentsByCity(cityFilter, filteredDepartmentsByName);

  useEffect(() => {
    const hash = asPath.split("#")[1];

    if (hash) {
      const hashSlug = getSlug(hash);
      const sectionElement = document.getElementById(hashSlug);

      if (sectionElement) {
        setOpenedSection(hashSlug);
      }
    }
  }, []);

  useEffect(() => {
    if (openedSection) {
      const sectionElement = document.getElementById(openedSection);
      sectionElement.scrollIntoView();
    }
  }, [openedSection]);

  return (
    <>
      <Breadcrumbs
        breadcrumbs={[
          {
            id: 1,
            level: 99,
            name: `Cariere - Locuri de munca ${websiteTitle}`,
          },
        ]}
      />

      <Heading
        as="h1"
        display="h1"
        title={content[websiteName].title}
        classesProp="mt-6 font-normal text-[22px] md:text-[28px] leading-[28px] md:leading-[36px] w-full text-center"
      />

      <div className="flex items-center justify-center w-full py-8 md:py-16 has-custom-start-on-bg">
        <picture>
          {content[websiteName].image.map((img) => (
            <Fragment key={img.path}>
              {!img.isDefault ? (
                <source srcSet={img.path} media={img.media} />
              ) : (
                <img
                  className="max-w-[714px]"
                  alt=""
                  sizes="100vw"
                  src={img.path}
                />
              )}
            </Fragment>
          ))}
        </picture>
      </div>

      <div className="text-base leading-relaxed lg:w-3/4 mx-auto">
        <div className="space-y-2 py-2 border-y mb-4">
          <Accordion
            expandedItems={openedSection ? [openedSection] : []}
            onToggle={onAccordionItemToggle}
          >
            <AccordionItem id="despre-noi">
              <AccordionItemHeader>
                <Heading as="h2" display="h2" title="Despre noi" />
              </AccordionItemHeader>
              <AccordionItemBody>
                {content[websiteName].mainContent}
              </AccordionItemBody>
            </AccordionItem>
          </Accordion>
        </div>

        <Heading
          as="h2"
          display="h2"
          title="Pozitii vacante"
          classesProp="mb-4"
        />

        <div className="md:flex flex-wrap py-2 mb-4">
          <div className="md:flex items-baseline md:mr-12 mb-4 md:mb-0">
            <label htmlFor="departmentFilter" className="mr-3">
              Departament
            </label>

            <select
              id="departmentFilter"
              value={departmentFilter}
              onChange={handleDepartmentFilterChange}
            >
              <option value="Toate" defaultValue={true}>
                Toate
              </option>
              {getDepartmentNames().map((departmentName, i) => (
                <option value={departmentName} key={`${departmentName}-${i}`}>
                  {departmentName}
                </option>
              ))}
            </select>
          </div>

          <div className="md:flex items-baseline">
            <label htmlFor="cityFilter" className="mr-3">
              Oras
            </label>

            <select
              id="cityFilter"
              value={cityFilter}
              onChange={handleCityFilterChange}
            >
              <option value="Toate">Toate</option>
              {getPositionsCityNames().map((cityName) => (
                <option value={cityName} key={cityName}>
                  {cityName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {filteredDepartmentsWithJobsByCity.length ? (
          <div className="space-y-2">
            <Accordion
              onToggle={onAccordionItemToggle}
              expandedItems={openedSection ? [openedSection] : []}
            >
              {filteredDepartmentsWithJobsByCity.map((department) =>
                department.jobs.map((job) => (
                  <div className="border rounded px-8" key={job.name}>
                    <AccordionItem id={getSlug(job.name)}>
                      <AccordionItemHeader>
                        <div
                          className={`flex flex-col flex-shrink-0 md:flex-row md:items-center text-sm py-4 ${
                            cityFilter === "Toate" ? "pr-12" : ""
                          }`}
                        >
                          <h2 className="flex-shrink-0 mb-3 md:mb-0 md:w-64 md:pr-6 font-medium capitalize">
                            {job.name.toLowerCase()}
                          </h2>

                          {cityFilter === "Toate" && (
                            <div className="w-full mb-2 md:mb-0">
                              Pozitii deschise in:{" "}
                              {job.locations &&
                                job.locations
                                  .map((location) => location.city)
                                  .join(", ")}
                            </div>
                          )}
                        </div>
                      </AccordionItemHeader>

                      <AccordionItemBody>
                        {job.content && Object.keys(job.content).length !== 0 && (
                          <div className="py-4 md:py-8 border-gray-light border-t">
                            {job.content.expectations &&
                              job.content.expectations.length !== 0 && (
                                <>
                                  <Heading
                                    as="h3"
                                    display="h4"
                                    title="Candidatul ideal"
                                    classesProp="text-red-brand mb-4"
                                  />
                                  <ul className="mb-4 text-sm">
                                    {job.content.expectations.map(
                                      (expectation, index) => (
                                        <li key={index}>{expectation}</li>
                                      )
                                    )}
                                  </ul>
                                </>
                              )}

                            {job.content.other_specifications &&
                              job.content.other_specifications.length !== 0 && (
                                <>
                                  <Heading
                                    as="h3"
                                    display="h4"
                                    title="Responsabilitati"
                                    classesProp="text-red-brand mb-4"
                                  />
                                  <ul className="mb-4 text-sm">
                                    {job.content.other_specifications.map(
                                      (specification, index) => (
                                        <li key={index}>{specification}</li>
                                      )
                                    )}
                                  </ul>
                                </>
                              )}

                            {job.content.benefits &&
                              job.content.benefits.length !== 0 && (
                                <>
                                  <Heading
                                    as="h3"
                                    display="h4"
                                    title="Beneficii"
                                    classesProp="text-red-brand mb-4"
                                  />
                                  <ul className="mb-4 text-sm">
                                    {job.content.benefits.map(
                                      (benefit, index) => (
                                        <li key={index}>{benefit}</li>
                                      )
                                    )}
                                  </ul>
                                </>
                              )}

                            {job.locations?.length !== 0 && (
                              <>
                                <Heading
                                  as="h3"
                                  display="h4"
                                  title="Alege orasul pentru care vrei sa aplici:"
                                  classesProp="text-red-brand mb-4"
                                />
                                <div className="flex flex-wrap">
                                  {job.locations?.map((location, index) => (
                                    <div className="mr-2 mt-2" key={index}>
                                      <Button
                                        title={location.city}
                                        onClick={() =>
                                          handleSelectJobTitle({
                                            job,
                                            location,
                                          })
                                        }
                                      />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </AccordionItemBody>
                    </AccordionItem>
                  </div>
                ))
              )}
            </Accordion>
          </div>
        ) : (
          <div>Nu exista pozitii vacante conform criteriilor selectate.</div>
        )}

        {content[websiteName].footerContent}
      </div>

      {selectedPosition && (
        <Modal
          id="jobApplication"
          header={`Aplica pentru pozitia ${selectedPosition.jobInfo.name} in ${selectedPosition.location.city}`}
          footer={
            <>
              <div className="text-sm mb-2">
                Te rugam sa atasezi CV-ul inainte de a trimite e-mail-ul.
              </div>
              <Button
                onClick={handleSubmitJobApplication}
                title="Adauga CV si aplica"
              />
              <button
                onClick={handleCancelJobApplication}
                className="ml-4 outline-none underline text-red-brand"
              >
                Renunta
              </button>
            </>
          }
        >
          <Form
            onSubmit={handleJobApplicationFormSubmit}
            render={({ handleSubmit }) => {
              setJobApplicationFormSubmitHandler(() => handleSubmit);
              return (
                <form
                  onSubmit={handleSubmit}
                  autoComplete="false"
                  noValidate={true}
                >
                  <div className="md:flex md:flex-wrap md:-mx-4">
                    <div className="mb-4 md:w-1/2 md:px-4">
                      <Field
                        component={InputText}
                        id="in_store_credit_request_last_name"
                        name="lastName"
                        label="Nume"
                        type="text"
                        required
                        validate={composeFormValidators(
                          isRequired(),
                          isAlphaNumeric(),
                          hasMaxLength(60)
                        )}
                      />
                    </div>

                    <div className="mb-4 md:w-1/2 md:px-4">
                      <Field
                        component={InputText}
                        id="in_store_credit_request_first_name"
                        name="firstName"
                        label="Prenume"
                        className="Form-input Form-wide"
                        required
                        validate={composeFormValidators(
                          isRequired(),
                          isAlphaNumeric(),
                          hasMaxLength(60)
                        )}
                      />
                    </div>

                    <div className="mb-4 md:w-1/2 md:px-4">
                      <Field
                        name="phoneNumber"
                        component={InputText}
                        label="Numar de telefon"
                        type="tel"
                        required
                        validate={composeFormValidators(
                          isRequired(),
                          isPhoneNumber()
                        )}
                      />
                    </div>
                  </div>

                  <div className="">
                    <Field
                      name="agree_personal_data_processing"
                      component={InputCheckbox}
                      label={
                        <>
                          Sunt de acord cu{" "}
                          <a
                            className="text-red-brand"
                            target="_blank"
                            href={`${cdn}/media/custom/upload/Nota_de_informare_privind_prelucrarea_datelor_personale_in_scop_de_recrutare.pdf`}
                            rel="noreferrer"
                          >
                            prelucrarea datelor
                          </a>{" "}
                          mele personale de catre societatile din Grupul Altex
                          in scopul recrutarii.
                        </>
                      }
                      type="checkbox"
                      required
                      validate={isRequired()}
                    />

                    <div className="text-sm">
                      Pentru informatii suplimentare, va rugam sa accesati
                      pagina{" "}
                      <a
                        href={`${baseUrl}/protectia-datelor-caracter-personal/pag/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-brand"
                      >
                        Protectia Datelor cu Caracter Personal
                      </a>
                      .
                    </div>
                  </div>
                </form>
              );
            }}
          />
        </Modal>
      )}
    </>
  );
};

CareersContainer.propTypes = {
  departmentsWithJobPositions: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default connect(null, { addModal, removeModal })(CareersContainer);
